import BEMHelper from '@ynap/bem-helper';
import { Button } from '@ynap/button';
import Notification from '@ynap/notification';
import { PriceWithPromotions as Price } from '@ynap/price';
import { OptimizelyFeature } from '@ynap/optimizely-utils'
import PropTypes from 'prop-types';
import React from 'react';

const bem = new BEMHelper('Totals');

const propTypes = {
    className: PropTypes.string,
    promotions: PropTypes.array,
    itemTotal: PropTypes.string,
    originalShippingPrice: PropTypes.string,
    finalShippingPrice: PropTypes.string,
    packaging: PropTypes.string,
    total: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    children: PropTypes.node,
    removePromotion: PropTypes.func,
    messages: PropTypes.shape({
        orderReview: PropTypes.shape({
            DDPTitle: PropTypes.func
        }),
        totals: PropTypes.shape({
            itemTotal: PropTypes.func,
            cashOnDelivery: PropTypes.func,
            shipping: PropTypes.func,
            packaging: PropTypes.func,
            total: PropTypes.string.func,
            remove: PropTypes.func,
            removeButtonLabel: PropTypes.func
        })
    }).isRequired,
    tax: PropTypes.string,
    duty: PropTypes.string,
    taxDutyInfo: PropTypes.string,
    noGrandTotalBorder: PropTypes.bool,
    storeCredit: PropTypes.string,
    savingsTotal: PropTypes.string,
    shippingTax: PropTypes.string,
    orderTaxes: PropTypes.object,
    enablePromotions: PropTypes.bool,
    codChargeAmount: PropTypes.string,
    ddpInfo: PropTypes.bool,
    showUsTaxUi: PropTypes.bool,
    isCheckoutV3: PropTypes.bool,
    priceWithTax: PropTypes.object,
    orderLevelTaxes: PropTypes.bool,
    defaultShippingOption: PropTypes.object,
    proximityMessage: PropTypes.object,
    variables: PropTypes.object,
    selectedShippingOption: PropTypes.object
};

function Totals(props) {
    const {
        className,
        itemTotal,
        originalShippingPrice,
        finalShippingPrice,
        packaging,
        total,
        currency,
        promotions,
        removePromotion,
        messages,
        children,
        tax,
        duty,
        taxDutyInfo,
        noGrandTotalBorder,
        storeCredit,
        savingsTotal,
        shippingTax,
        orderTaxes,
        enablePromotions,
        codChargeAmount,
        ddpInfo,
        priceWithTax,
        showUsTaxUi = false,
        orderLevelTaxes = false,
        isCheckoutV3,
        selectedShippingOption = null
    } = props;

    const finalTax = tax ? tax : '$0';

    return (
        <div className={bem(null, null, className)}>
            <ul className={bem('orderSummary')}>
                {/* showUsTaxUi and orderLevelTaxes allow for the different optimizely variations we'll be testing.
                    More on that here: https://confluence.nap/pages/viewpage.action?pageId=479591500
                 */}
                {itemTotal && (!showUsTaxUi || orderLevelTaxes) ? (
                    <li className={bem('item', 'itemTotal')}>
                        <span className={bem('description')}>{messages.totals.itemTotal()}</span>
                        <span className={bem('amount')}>{itemTotal}</span>
                    </li>
                ) : null}

                {showUsTaxUi && !orderLevelTaxes && priceWithTax ? (
                    <li className={bem('item', 'itemTotal')}>
                        <span className={bem('description')}>{messages.totals.itemTotal()}</span>
                        <span className={bem('amount')}>{priceWithTax}</span>
                    </li>
                ) : null}

                {showUsTaxUi && orderLevelTaxes ? (
                    <li className={bem('item', 'tax')}>
                        <span className={bem('description')}>{messages.totals.tax()}</span>
                        <span className={bem('amount')}>{finalTax}</span>
                    </li>
                ) : null}

                {(originalShippingPrice || finalShippingPrice) && !!selectedShippingOption ? (
                    <li className={bem('item', 'shipping')}>
                        <OptimizelyFeature feature={[
                            'web00149_proximitymessage_sbmb_desktop', // MRP Desktop
                            'web00158_proximitymessage_sbmb_mweb', // MRP Mobile
                            'web00148_proximitymessage_sbmb_desktop', // NAP Desktop
                            'web00157_proximitymessage_sbmb_mweb', // NAP Mobile
                            'web00150_proximitymessage_sbmb_desktop', // TON Desktop
                            'web00159_proximitymessage_sbmb_mweb' // TON Mobile
                        ]} {...props}>
                            {(props) => {
                                const { variables, proximityMessage } = props
                                const withShippingMode = variables?.enabled && proximityMessage?.proximityAmount

                                return <span className={bem('description')}>{messages.totals.shipping({ method: withShippingMode ? selectedShippingOption.name : null })}</span>
                            }}

                        </OptimizelyFeature>
                        <Price className={bem('price')} markedDownPrice={originalShippingPrice} finalPrice={finalShippingPrice} shipping={true} />
                    </li>
                ) : null}
                {codChargeAmount ? (
                    <li className={bem('item', 'cod')}>
                        <span className={bem('description')}>{messages.totals.cashOnDelivery()}</span>
                        <span className={bem('amount')}>{codChargeAmount}</span>
                    </li>
                ) : null}
                {storeCredit ? (
                    <li className={bem('item', 'storeCredit')}>
                        <span className={bem('description')}>{messages.totals.storeCreditLabel()}</span>
                        <span className={bem('amount')}>{messages.totals.storeCredit({ storeCredit })}</span>
                    </li>
                ) : null}
                {tax && !orderLevelTaxes && !showUsTaxUi ? (
                    <li className={bem('item', 'tax')}>
                        <span className={bem('description')}>{messages.totals.tax()}</span>
                        <span className={bem('amount')}>{tax}</span>
                    </li>
                ) : null}
                {duty ? (
                    <li className={bem('item', 'duty')}>
                        <span className={bem('description')}>{messages.totals.duty()}</span>
                        <span className={bem('amount')}>{duty}</span>
                    </li>
                ) : null}
                {shippingTax ? (
                    <li className={bem('item', 'shippingTax')}>
                        <span className={bem('description')}>{messages.totals.shippingTax()}</span>
                        <span className={bem('amount')}>{shippingTax}</span>
                    </li>
                ) : null}
                {packaging ? (
                    <li className={bem('item', 'packaging')}>
                        <span className={bem('description')}>{messages.totals.packaging()}</span>
                        <span className={bem('amount')}>{packaging}</span>
                    </li>
                ) : null}
                {enablePromotions && promotions && promotions.length > 0
                    ? promotions.map(promo => (
                        <li className={bem('item', 'promotion')} key={promo.code}>
                            <span className={bem('description', 'promotion')}>
                                <span className={bem('promotionCode')}>{promo.code}</span>
                                <span className={bem('promotionDescription')}>{promo.description}</span>
                                {removePromotion ? (
                                    <Button
                                        className={bem('remove')}
                                        aria-label={messages.totals.removeButtonLabel()}
                                        onClick={() => removePromotion(promo.code)}
                                    >
                                        {messages.totals.remove && messages.totals.remove()}
                                    </Button>
                                ) : null}
                            </span>
                            <span className={bem('promotionName')}>{promo.description}</span>
                        </li>
                    ))
                    : null}
                {orderTaxes?.BROKERAGE_FEE ? (
                    <li className={bem('item', 'tax')}>
                        <span className={bem('description')}>{messages.totals.brokerage()}</span>
                        <span className={bem('amount')}>{orderTaxes.BROKERAGE_FEE}</span>
                    </li>
                ) : null}
                {children ? <li className={bem('item')}>{children}</li> : null}
                <li className={bem('item', { total: true, noGrandTotalBorder, ddpInfo, isCheckoutV3 })}>
                    <span className={bem('description')}>
                        <span className={bem('total')}>{messages.totals.total()}</span>
                        <span className={bem('currency')}>({currency})</span>
                    </span>
                    <span className={bem('amountTotal')}>{total}</span>
                </li>
            </ul>
            {messages.totals.savingsTotal && savingsTotal ? (
                <div className={bem('savingsTotal')}>
                    {messages.totals.savingsTotal()}
                    <span className={bem('savingsTotal', 'amount')}>{savingsTotal}</span>
                </div>
            ) : null}

            {taxDutyInfo ? <Notification className={bem('info')} type="info" message={taxDutyInfo} /> : null}

            {messages.orderReview?.DDPTitle && ddpInfo && <div className={bem('ddpInfo')}>{messages.orderReview.DDPTitle()}</div>}
        </div>
    );
}

Totals.propTypes = propTypes;

export default Totals;
